import {
  ApiAlertCondition,
  ApiAlertConditionGroup,
  ApiAlertIntervalType,
  ApiAlertResponse,
  ApiConditionValueType,
  ApiMetric,
} from "@incendium/api";
import { Delete } from "@mui/icons-material";
import {
  Button,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SpacedList, { SpacedLinkButton } from "Components/UI/SpacedList";
import { cell1Icon } from "consts";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { friendlyDateTime } from "Helpers/dates";
import { friendlyOperator } from "Helpers/operators";
import React from "react";

const formatAlertSchedule = (alert: ApiAlertResponse) => {
  const interval = alert.interval || 1;
  switch (alert.intervalType) {
    case ApiAlertIntervalType.ALERT_PERIOD_TYPE_ASAP:
      return "ASAP";
    case ApiAlertIntervalType.ALERT_PERIOD_TYPE_HOURLY:
      return interval === 1 ? "Hourly" : `Every ${interval} hours`;
    case ApiAlertIntervalType.ALERT_PERIOD_TYPE_DAILY:
      return interval === 1 ? "Daily" : `Every ${interval} days`;
    case ApiAlertIntervalType.ALERT_PERIOD_TYPE_WEEKLY:
      return interval === 1 ? "Weekly" : `Every ${interval} weeks`;
    case ApiAlertIntervalType.ALERT_PERIOD_TYPE_MONTHLY:
      return interval === 1 ? "Monthly" : `Every ${interval} months`;
    default:
      return "—";
  }
};

const formatConditionValue = (condition: ApiAlertCondition) => {
  const value = condition.value || "";
  if (condition.valueType === ApiConditionValueType.PERCENTAGE_DIFFRENCE) {
    return `${value}%`;
  }
  return value;
};

const AlertConditionsCell = ({
  groups,
}: {
  groups: ApiAlertConditionGroup[];
}) => {
  const metricToName = useMetricName();

  if (!groups.length) {
    return <TableCell>—</TableCell>;
  }

  return (
    <TableCell>
      {groups.map((group, idx) => {
        const conditions = group.conditions || [];
        return (
          <div key={group.id || idx}>
            <Typography
              display="inline"
              variant="caption"
              color="textSecondary"
            >
              {conditions.map((condition, i) => (
                <React.Fragment key={condition.id || i}>
                  <Typography
                    display="inline"
                    variant="caption"
                    color="textSecondary"
                  >
                    <Typography
                      component="span"
                      variant="caption"
                      color="textSecondary"
                      fontWeight="bold"
                    >
                      {metricToName(condition.metric as ApiMetric)}
                    </Typography>{" "}
                    {friendlyOperator(condition.operator)}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="textSecondary"
                      fontWeight="bold"
                    >
                      {formatConditionValue(condition)}
                    </Typography>
                  </Typography>
                  {i + 1 < conditions.length && <strong> & </strong>}
                </React.Fragment>
              ))}
            </Typography>
            {idx + 1 < groups.length && (
              <Typography
                variant="body2"
                color="textSecondary"
                fontWeight="bold"
              >
                -- Or --
              </Typography>
            )}
          </div>
        );
      })}
    </TableCell>
  );
};

interface IAlertsListProps {
  alerts: ApiAlertResponse[];
  onNew: () => void;
  onEdit: (alert: ApiAlertResponse) => void;
  onDelete: (alert: ApiAlertResponse) => void;
}

function AlertsList({ alerts, onNew, onEdit, onDelete }: IAlertsListProps) {
  return (
    <SpacedList
      title="Alerts List"
      isEmpty={!alerts || alerts.length === 0}
      action={
        <Button variant="contained" color="primary" onClick={onNew}>
          New Alert
        </Button>
      }
    >
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Schedule</TableCell>
          <TableCell>Recipients</TableCell>
          <TableCell>Conditions</TableCell>
          <TableCell>Last Checked</TableCell>
          <TableCell>Last Notified</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {alerts.map((alert) => {
          const groups =
            alert.requirements?.flatMap((r) => r.conditionGroups || []) || [];
          return (
            <TableRow key={alert.id}>
              <TableCell sx={{ width: 260 }}>
                <SpacedLinkButton onClick={() => onEdit(alert)}>
                  {alert.name || "Untitled alert"}
                </SpacedLinkButton>
                {alert.description ? (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    noWrap
                    sx={{ maxWidth: 260 }}
                    title={alert.description}
                  >
                    {alert.description}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>{formatAlertSchedule(alert)}</TableCell>
              <TableCell
                sx={{ maxWidth: 220 }}
                title={(alert.emails || []).join(", ")}
              >
                {(alert.emails || []).length > 0
                  ? (alert.emails || []).join(", ")
                  : "—"}
              </TableCell>
              <AlertConditionsCell groups={groups} />
              <TableCell>
                {alert.checkedAt ? friendlyDateTime(alert.checkedAt) : "—"}
              </TableCell>
              <TableCell>
                {alert.notifiedAt ? friendlyDateTime(alert.notifiedAt) : "—"}
              </TableCell>
              <TableCell align="right" style={{ width: cell1Icon }}>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onDelete(alert)}
                >
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </SpacedList>
  );
}

export default AlertsList;
